import React, { useContext, useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import { ThemeContext } from '../../../auth';
import Select, { components } from 'react-select';
import { Col, Row } from 'react-bootstrap';
import { axiosAuth } from '../../../api';
import Swal from 'sweetalert2';
import { useLocation } from 'react-router-dom';
import { FaRegCalendar } from 'react-icons/fa6';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

function ChartLine({ id, accessToken }) {

    const location = useLocation();
    const { theme } = useContext(ThemeContext);
    const today = new Date;
    const year = today.getFullYear();

    const [userDataLineChart, setUserDataLineChart] = useState({});

    const retrieveLineChart = () => {
        axiosAuth.get(
            location.pathname.startsWith('/dashboard-spv') ?
                `/api/analytics/summary/sales?&year=${year}` :
                `/api/analytics/summary/sales?userId=${id}&year=${year}`
            ,
            {
                headers:
                {
                    'Access-Control-Allow-Origin': '*',
                    'Content-Type': 'application/json',
                    withCredentials: true,
                    Authorization: `Bearer ${accessToken}`,
                },

            })
            .then(res => {

                setUserDataLineChart(res.data.data);

            }).catch(err => {
                console.error(err);

                Swal.fire({
                    icon: "error",
                    title: "Oops...",
                    text: 'Failed to collect data, try again later!',
                });

            })
    };

    useEffect(() => {
        if (accessToken !== undefined && id !== undefined && year !== undefined) retrieveLineChart();
    }, [accessToken, id, year]);

    const labels = [
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];

    const [selectedYear, setSelectedYear] = useState(null);

    const currentYear = new Date().getFullYear();
    const yearOptions = Array.from({ length: currentYear - 2020 + 1 }, (_, i) => {
        const year = 2020 + i;
        return { value: year, label: year.toString() };
    });

    const handleSelectYear = selectedOption => {
        setSelectedYear(selectedOption);
    };

    useEffect(() => {
        const fetchLineChart = async () => {
            if (!selectedYear) return;

            try {
                const response = await axiosAuth.get(
                    location.pathname.startsWith('/dashboard-spv') ?
                        `/api/analytics/summary/sales?year=${selectedYear?.value}` :
                        `/api/analytics/summary/sales?userId=${id}&year=${selectedYear?.value}`
                    ,
                    {
                        headers: {
                            'Access-Control-Allow-Origin': '*',
                            'Content-Type': 'application/json',
                            withCredentials: true,
                            Authorization: `Bearer ${accessToken}`,
                        },
                    }
                );

                setUserDataLineChart(response.data.data);

            } catch (err) {
                console.error(err);

                Swal.fire({
                    icon: "error",
                    title: "Oops...",
                    text: 'Failed to collect data, try again later!',
                });

            }
        };

        fetchLineChart();
    }, [selectedYear]);

    const totalLeadValue = labels.map((month) => userDataLineChart?.totalLead?.[month] || 0);
    const successLeadValue = labels.map((month) => userDataLineChart?.totalSuccessLead?.[month] || 0);
    const failedLeadValue = labels.map((month) => userDataLineChart?.totalFailLead?.[month] || 0);

    const data = {
        labels: labels,
        datasets: [
            {
                lineTension: 0.3,
                label: `Total Lead`,
                borderWidth: 2,
                pointBorderColor: 'transparent',
                borderColor: theme === 'light' ? '#12B3ED' : '#FF6F61',
                pointBackgroundColor: theme === 'light' ? '#12B3ED' : '#FF6F61',
                pointStyle: 'circle',
                pointRadius: 6,
                data: totalLeadValue,
                fill: true,
                backgroundColor: (context) => {
                    const ctx = context.chart.ctx;
                    const gradient = ctx.createLinearGradient(0, 0, 0, 200);
                    gradient.addColorStop(0, theme === 'light' ? 'rgba(97, 210, 255, 0.5)' : 'rgba(255, 111, 97, 0.5)');
                    gradient.addColorStop(1, theme === 'light' ? 'rgba(97, 210, 255, 0.1)' : 'rgba(255, 111, 97, 0)');
                    return gradient;
                },
            },
            {
                lineTension: 0.3,
                label: `Total Successfull Lead`,
                borderWidth: 2,
                pointBorderColor: 'transparent',
                borderColor: '#6B48FF',
                pointBackgroundColor: '#6B48FF',
                pointStyle: 'rectRot',
                pointRadius: 6,
                data: successLeadValue,
                fill: true,
                backgroundColor: (context) => {
                    const ctx = context.chart.ctx;
                    const gradient = ctx.createLinearGradient(0, 0, 0, 200);
                    gradient.addColorStop(0, 'rgba(107, 72, 255, 0.5)');
                    gradient.addColorStop(1, 'rgba(107, 72, 255, 0)');
                    return gradient;
                },
            },
            {
                lineTension: 0.3,
                label: `Total Failed Lead`,
                borderWidth: 2,
                pointBorderColor: 'transparent',
                borderColor: '#FF4081',
                pointBackgroundColor: '#FF4081',
                pointStyle: 'rectRot',
                pointRadius: 6,
                data: failedLeadValue,
                fill: true,
                backgroundColor: (context) => {
                    const ctx = context.chart.ctx;
                    const gradient = ctx.createLinearGradient(0, 0, 0, 200);
                    gradient.addColorStop(0, 'rgba(255, 64, 129, 0.5)');
                    gradient.addColorStop(1, 'rgba(255, 64, 129, 0)');
                    return gradient;
                },
            },
        ],
    };

    const config = {
        type: 'line',
        data: data,
        responsive: true,
        scales: {
            y: {
                beginAtZero: true,
                grid: {
                    display: true
                },
                ticks: {
                    color: theme === 'light' ? 'white' : '#222',
                    font: {
                        family: 'Poppins-Regular',
                    },
                    callback: function (value) {
                        if (value >= 1000000) {
                            return `${(value / 1000000).toFixed(1)}M`;
                        } else if (value >= 1000) {
                            return `${(value / 1000).toFixed(1)}K`;
                        }
                        return value;
                    }
                },
            },
            x: {
                ticks: {
                    color: theme === 'light' ? 'white' : '#222',
                    font: {
                        family: 'Poppins-Regular',
                    },
                },
                grid: {
                    display: true
                },
            },
        },
        plugins: {
            legend: {
                display: true,
                position: 'bottom',
                labels: {
                    color: theme === 'light' ? 'white' : '#222',
                    font: {
                        family: 'Poppins-Regular',
                    },
                },
            },
            tooltip: {
                mode: 'index',
                intersect: false,
            },
        },
    };

    const customDropdownIndicator = (props) => {
        return (
            <components.DropdownIndicator {...props}>
                <FaRegCalendar style={{ color: theme === 'light' ? '#FFFFFF' : '#000', fontSize: '14px' }} />
            </components.DropdownIndicator>
        );
    };

    const selectStyles = {
        control: (provided, state) => ({
            ...provided,
            height: '20px',
            width: '135px',
            border: state.isFocused ? '1px solid #80bdff' : '1px solid #ced4da',
            background: theme === 'light' ? '#212529' : '#FFFFFF',
            boxShadow: state.isFocused ? '0 0 0 0.3rem rgba(0, 123, 255, 0.25)' : null,
            '&:hover': {
                borderColor: '#80bdff',
            },
            fontSize: '13px',
            fontFamily: 'Roboto-Regular',
            borderRadius: '10px'
        }),
        singleValue: (provided, state) => ({
            ...provided,
            color: theme === 'light' ? (state.isFocused ? 'red' : 'white') : (state.isFocused ? 'red' : '#222'),
            fontSize: '13px',
        }),
        option: (provided, state) => ({
            ...provided,
            color: state.isSelected ? '#fff' : '#333',
            background: state.isSelected ? '#007bff' : '#fff',
            fontFamily: 'Roboto-Regular',
            fontSize: '13px',
        }),
        input: (provided) => ({
            ...provided,
            color: theme === 'light' ? '#FFFFFF' : '#222',
            fontFamily: 'Roboto-Regular',
            fontSize: '13px',
        }),
        menu: (provided) => ({
            ...provided,
            zIndex: 9999,
        }),
        menuList: (provided) => ({
            ...provided,
            padding: '0',
        }),
        dropdownIndicator: (provided) => ({
            ...provided,
            padding: '4px',
        }),
    };

    return (
        <div>
            <div className='mb-3'>
                <Row className="align-items-center">
                    <Col xs={6} className='text-start my-auto'>
                        <p
                            className="ps-1 pt-2 text-start"
                            style={{
                                marginBottom: '0px',
                                fontFamily: 'Poppins-Bold',
                                fontSize: '20px',
                                color: theme === 'light' ? '#FFFFFF' : '#222',
                            }}
                        >
                            Lead Amount
                        </p>
                    </Col>
                    <Col xs={6} className='d-flex justify-content-end'>
                        <Select
                            name="year"
                            options={yearOptions}
                            value={selectedYear}
                            onChange={handleSelectYear}
                            styles={selectStyles}
                            components={{ DropdownIndicator: customDropdownIndicator }}
                            placeholder={year}
                            required
                        />
                    </Col>
                </Row>
            </div>
            <Line
                data={data}
                options={config}
                height={100}
                className=''
            />
        </div>
    )
}

export default ChartLine
